<template>
  <div class="grid grid-rows mb-4 order-history mx-auto" id="patient-messages-root">
    <!-- Message List View -->
    <div v-if="!selectedMessage">
      <div v-if="messages.length === 0" class="grid grid-rows p-6 mb-6 mt-4 callout">
        Er zijn geen berichten beschikbaar voor deze patiënt.
      </div>

      <div v-else class="rounded-lg">
        <div
          class="flex flex-row border-b border-gray-200"
          v-for="(message, index) in messages"
          :key="`message-${index}`"
          :class="{ 'font-bold': !message.read }"
        >
          <div class="w-full cursor-pointer" @click.prevent="selectMessage(message)">
            <div class="flex flex-col md:flex-row relative">
              <div class="p-2 md:p-4 text-base text-tdefault-color w-full md:w-7/12">
                {{ message.subject || 'Geen onderwerp' }}
              </div>
              <div class="p-2 md:p-4 text-base text-tdefault-color w-full md:w-2/12">
                {{ message.patient_name || '-' }}
              </div>
              <div class="p-2 md:p-4 text-base text-tdefault-color w-full md:w-2/12">
                {{ $formatDate ? $formatDate(message.received_at) : formatDate(message.received_at) }}
              </div>
              <div
                class="p-2 md:-4 text-base text-primary-default font-bold cursor-pointer md:w-2/12 relative content-center cursor-pointer hidden md:block"
                @click.prevent="selectMessage(message)"
              >
                <span class="float-left md:float-right">Bekijk bericht</span>
              </div>
            </div>
          </div>
          <div class="p-2 md:p-4 text-base text-tdefault-color w-4 content-center relative">
            <div class="relative inline-block text-left">
              <button
                @click.stop="toggleDropdown(index)"
                type="button"
                class="inline-flex items-center justify-center rounded-full p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pdsPrimaryColor focus:ring-offset-2"
                :aria-expanded="openDropdownIndex === index"
                aria-haspopup="true"
              >
                <span class="sr-only">Open options</span>
                <EllipsisVerticalIcon class="h-5 w-5" aria-hidden="true" />
              </button>
              <div
                v-if="openDropdownIndex === index"
                class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                role="menu"
                aria-orientation="vertical"
                aria-labelledby="menu-button"
              >
                <div class="py-1" role="none">
                  <a
                    v-if="message.read"
                    href="#"
                    @click.stop.prevent="markAsUnread(message, index)"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Markeer als ongelezen
                  </a>
                  <a
                    v-else
                    href="#"
                    @click.stop.prevent="markAsRead(message, index)"
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    Markeer als gelezen
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Message Detail View -->
    <div v-else class="p-4">
      <div class="mb-3">
        <button
          @click.prevent="selectedMessage = null"
          class="flex items-center text-pdsPrimaryColor hover:text-pdsPrimaryColor-700 font-medium"
        >
          <ArrowLeftIcon class="h-5 w-5 flex-shrink-0 mr-2 text-primary-default" aria-hidden="true" />
          Terug naar alle berichten
        </button>
      </div>

      <div class="space-y-4">
        <div class="text-xl font-bold text-talternative-color">
          {{ selectedMessage.subject || 'Geen onderwerp' }}
        </div>

        <div class="flex items-center">
          <span class="font-bold text-tdefault-color">Datum:</span>
          <span class="text-tdefault-color ml-2">
            {{ $formatDate ? $formatDate(selectedMessage.received_at) : formatDate(selectedMessage.received_at) }}
          </span>
        </div>

        <div v-if="selectedMessage.patient_name" class="flex items-center">
          <span class="font-bold text-tdefault-color">Patiënt:</span>
          <span
            class="text-tdefault-color ml-2 cursor-pointer underline"
            @click.prevent="$emit('switch-view', 'dashboard')"
          >
            {{ selectedMessage.patient_name }}
          </span>
        </div>

        <div
          class="pt-2 text-gray-900 leading-relaxed"
          v-html="selectedMessage.body || selectedMessage.content || 'Geen inhoud beschikbaar'"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { EllipsisVerticalIcon, ArrowLeftIcon } from '@heroicons/vue/20/solid';
import { currentUserIdentity$, activeDepartment$ } from '@/services';
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'PatientMessages',
  components: {
    EllipsisVerticalIcon,
    ArrowLeftIcon
  },
  props: {
    patientId: {
      type: Number
    }
  },
  emits: ['switch-view', 'unread-count'],
  setup() {
    const openDropdownIndex = ref(null);

    const toggleDropdown = (index) => {
      openDropdownIndex.value = openDropdownIndex.value === index ? null : index;
    };

    const closeDropdown = () => {
      openDropdownIndex.value = null;
    };

    const handleClickOutside = (event) => {
      const el = document.getElementById('patient-messages-root');
      if (el && !el.contains(event.target)) {
        closeDropdown();
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      openDropdownIndex,
      toggleDropdown,
      closeDropdown
    };
  },
  data() {
    return {
      messages: [],
      selectedMessage: null,
      user: null,
      activeDepartment: null,
      userSubscription: null,
      departmentSubscription: null,
      unreadCount: 0,
      showOptions: false
    };
  },
  computed: {
    baseUrl() {
      return window.location.origin;
    }
  },
  watch: {
    messages() {
      this.unreadCount = this.messages.filter((msg) => !msg.read).length;
      this.$emit('unread-count', this.unreadCount);
    }
  },
  async created() {
    // Subscribe to current user identity
    this.userSubscription = currentUserIdentity$.subscribe((currentUser) => {
      this.user = currentUser;
      if (currentUser && currentUser.id && this.activeDepartment) {
        this.fetchMessages();
      }
    });

    // Subscribe to active department
    this.departmentSubscription = activeDepartment$.subscribe((department) => {
      this.activeDepartment = department;
      if (this.user && this.user.id && department) {
        this.fetchMessages();
      }
    });
  },
  beforeUnmount() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.departmentSubscription) {
      this.departmentSubscription.unsubscribe();
    }
  },
  methods: {
    async fetchMessages() {
      if (!this.user || !this.user.id || !this.activeDepartment) {
        return;
      }

      try {
        let url = `${this.baseUrl}/api/sitecore/v2/Notification/GetInboxMessages?patientId=${this.patientId}`;

        // Add userId if available
        if (this.user && this.user.id) {
          url += `&userId=${this.user.id}`;
        }

        // Add departmentId if available
        if (this.activeDepartment && this.activeDepartment.ppwRecId) {
          url += `&departmentId=${this.activeDepartment.ppwRecId}`;
        }

        const { data } = await axios.get(url);
        this.messages = data || [];
      } catch (error) {
        console.error('Error fetching messages:', error);
        this.messages = [];
      }
    },
    async selectMessage(message) {
      this.selectedMessage = message;

      // Mark message as read if it's not already read
      if (!message.read) {
        // Update the message in the array immediately to unbold it (optimistic update)
        const messageIndex = this.messages.findIndex((m) => m.id === message.id);
        if (messageIndex !== -1) {
          this.messages = this.messages.map((m, idx) => (idx === messageIndex ? { ...m, read: true } : m));
        }
        // Also update the selected message
        message.read = true;

        // Mark as read on the server in the background
        try {
          await axios.post(`${this.baseUrl}/api/sitecore/v2/Notification/MarkMessageAsRead`, {
            patientId: this.patientId,
            messageId: message.id,
            inboxId: message.inbox_id || message.inboxId,
            userId: this.user.id,
            departmentId: this.activeDepartment ? this.activeDepartment.ppwRecId : null
          });
        } catch (error) {
          console.error('Error marking message as read:', error);
          // Don't revert - keep the optimistic update
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'Onbekend';
      try {
        return new Date(dateString).toLocaleDateString('nl-NL');
      } catch (error) {
        return 'Onbekend';
      }
    },
    async markAsRead(message, index) {
      this.closeDropdown();

      if (message.read) {
        return; // Already read
      }

      // Update the message in the array immediately (optimistic update)
      const messageIndex = this.messages.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        this.messages = this.messages.map((m, idx) => (idx === messageIndex ? { ...m, read: true } : m));
      }
      // Also update the message object
      message.read = true;

      // Mark as read on the server
      try {
        await axios.post(`${this.baseUrl}/api/sitecore/v2/Notification/MarkMessageAsRead`, {
          patientId: this.patientId,
          messageId: message.id,
          inboxId: message.inbox_id || message.inboxId,
          userId: this.user.id,
          departmentId: this.activeDepartment ? this.activeDepartment.ppwRecId : null
        });
      } catch (error) {
        console.error('Error marking message as read:', error);
        // Revert optimistic update on error
        if (messageIndex !== -1) {
          this.messages = this.messages.map((m, idx) => (idx === messageIndex ? { ...m, read: false } : m));
        }
        message.read = false;
      }
    },
    async markAsUnread(message, index) {
      this.closeDropdown();

      if (!message.read) {
        return; // Already unread
      }

      // Update the message in the array immediately (optimistic update)
      const messageIndex = this.messages.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        this.messages = this.messages.map((m, idx) => (idx === messageIndex ? { ...m, read: false } : m));
      }
      // Also update the message object
      message.read = false;

      // Mark as unread on the server
      try {
        await axios.post(`${this.baseUrl}/api/sitecore/v2/Notification/MarkMessageAsUnread`, {
          patientId: this.patientId,
          messageId: message.id,
          inboxId: message.inbox_id || message.inboxId,
          userId: this.user.id,
          departmentId: this.activeDepartment ? this.activeDepartment.ppwRecId : null
        });
      } catch (error) {
        console.error('Error marking message as unread:', error);
        // Revert optimistic update on error
        if (messageIndex !== -1) {
          this.messages = this.messages.map((m, idx) => (idx === messageIndex ? { ...m, read: false } : m));
        }
        message.read = false;
      }
    }
  }
};
</script>
